import type {PendingCapableAttachment, UploadFailure} from '@Components/ai-prompt-box/components/ai-prompt-box-attachments/ai-prompt-box-attachments.types';
import type {PendingAttachmentResult} from '@Components/ai-prompt-box/libraries/ai-prompt-box-attachment.library';
import {clearPendingUpload, getPendingUploadFile, registerPendingUpload, updatePendingUploadPromise} from '@Components/ai-prompt-box/libraries/ai-prompt-box-pending-uploads.library';
import {getMaxAllowedUploadFileSize, getReadableMaxAllowedUploadFileSize} from '@Libraries/user-media-library';
import {GROWL_TYPE, showMessageGrowl} from '@Components/message-growl';
import {noop} from '@Utils/general.util';

export interface FileUploadPipelineState {
  promptBoxId: string;
  getAttachmentCount: () => number;
  setActive: () => void;
  addPendingAttachments: (attachments: PendingCapableAttachment[]) => void;
  resolvePendingAttachment: (localId: string, resolved: PendingCapableAttachment) => void;
  markPendingAttachmentFailed: (localId: string, failure?: UploadFailure) => void;
  markPendingAttachmentUploading: (localId: string) => void;
  markPendingAttachmentProcessing: (localId: string) => void;
}

export interface FileUploadPipelineConfig<TResponse, TAttachment extends PendingCapableAttachment> {
  maxAttachments: number;
  createPendingAttachment: (file: File) => PendingAttachmentResult<TAttachment>;
  uploadFile: (file: File, onAccepted: VoidFunction) => Promise<TResponse>;
  resolveAttachment: (response: TResponse, pending: TAttachment) => TAttachment;
  onAttachmentResolved?: (resolved: TAttachment) => void;
  onUploadFailed?: (error: unknown) => void;
  describeUploadFailure?: (error: unknown) => UploadFailure;
}

interface UploadableFileItem<TResponse, TAttachment extends PendingCapableAttachment> {
  file: File;
  attachment: TAttachment;
  localId: string;
  resolveFn: (response: TResponse) => void;
  rejectFn: () => void;
}

interface PendingFileItem<TResponse, TAttachment extends PendingCapableAttachment> extends UploadableFileItem<TResponse, TAttachment> {
  blobUrl: string;
  promise: Promise<TResponse>;
}

interface DeferredUpload<TResponse> {
  promise: Promise<TResponse>;
  resolveFn: (response: TResponse) => void;
  rejectFn: () => void;
}

interface FilteredFiles {
  accepted: File[];
  oversized: File[];
}

const ERROR_GROWL_INTERVAL_MS = 7000;

export const handleFilesUpload = <TResponse, TAttachment extends PendingCapableAttachment>(files: File[], state: FileUploadPipelineState, config: FileUploadPipelineConfig<TResponse, TAttachment>): number => {
  const currentCount = state.getAttachmentCount();
  if (currentCount >= config.maxAttachments) {
    showAttachmentLimitGrowl(config.maxAttachments);
    return 0;
  }

  const filtered = filterFiles(files, getMaxAllowedUploadFileSize());
  if (filtered.oversized.length > 0) {
    showOversizedFileGrowl(filtered.oversized.length);
  }
  if (filtered.accepted.length === 0) {
    return 0;
  }

  const filesToUpload = filtered.accepted.slice(0, config.maxAttachments - currentCount);
  if (filesToUpload.length < filtered.accepted.length) {
    showAttachmentLimitGrowl(config.maxAttachments);
  }
  const pendingItems = filesToUpload.map((file) => buildPendingItem(file, config));

  state.addPendingAttachments(pendingItems.map((item) => item.attachment));
  pendingItems.forEach((item) => {
    registerPendingUpload(state.promptBoxId, item.localId, item.blobUrl, item.promise, item.file);
  });
  state.setActive();

  void runUploadBatch(pendingItems, state, config);

  return pendingItems.length;
};

export const retryFileUpload = <TResponse, TAttachment extends PendingCapableAttachment>(
  localId: string,
  pending: TAttachment,
  state: FileUploadPipelineState,
  config: FileUploadPipelineConfig<TResponse, TAttachment>
): void => {
  const file = getPendingUploadFile(state.promptBoxId, localId);
  if (!file) {
    return;
  }

  const {promise, resolveFn, rejectFn} = createDeferredUpload<TResponse>();
  updatePendingUploadPromise(state.promptBoxId, localId, promise);
  state.markPendingAttachmentUploading(localId);

  void uploadSingleFile({file, localId, resolveFn, rejectFn, attachment: pending}, state, config);
};

/**
 * The pending promise stored in the registry is only awaited later, when the user submits.
 * Until then we attach a no-op rejection handler so a failed upload doesn't surface as an
 * "Uncaught (in promise)" warning while the user is still deciding what to do.
 */
const createDeferredUpload = <TResponse>(): DeferredUpload<TResponse> => {
  let resolveFn!: (response: TResponse) => void;
  let rejectFn!: () => void;
  const promise = new Promise<TResponse>((resolve, reject) => {
    resolveFn = resolve;
    rejectFn = reject;
  });
  promise.catch(noop);
  return {promise, resolveFn, rejectFn};
};

const buildPendingItem = <TResponse, TAttachment extends PendingCapableAttachment>(
  file: File,
  config: FileUploadPipelineConfig<TResponse, TAttachment>
): PendingFileItem<TResponse, TAttachment> => {
  const {attachment, localId, blobUrl} = config.createPendingAttachment(file);
  const {promise, resolveFn, rejectFn} = createDeferredUpload<TResponse>();
  return {file, attachment, localId, blobUrl, resolveFn, rejectFn, promise};
};

const runUploadBatch = async <TResponse, TAttachment extends PendingCapableAttachment>(items: PendingFileItem<TResponse, TAttachment>[], state: FileUploadPipelineState, config: FileUploadPipelineConfig<TResponse, TAttachment>): Promise<void> => {
  await Promise.allSettled(items.map((item) => uploadSingleFile(item, state, config)));
};

const uploadSingleFile = async <TResponse, TAttachment extends PendingCapableAttachment>(item: UploadableFileItem<TResponse, TAttachment>, state: FileUploadPipelineState, config: FileUploadPipelineConfig<TResponse, TAttachment>): Promise<void> => {
  try {
    const response = await config.uploadFile(item.file, (): void => {
      state.markPendingAttachmentProcessing(item.localId);
    });
    onUploadSuccess(response, item, state, config);
  } catch (error) {
    onUploadError(item, state, config, error);
  }
};

const onUploadSuccess = <TResponse, TAttachment extends PendingCapableAttachment>(response: TResponse, item: UploadableFileItem<TResponse, TAttachment>, state: FileUploadPipelineState, config: FileUploadPipelineConfig<TResponse, TAttachment>): void => {
  const resolved = config.resolveAttachment(response, item.attachment);
  state.resolvePendingAttachment(item.localId, resolved);
  clearPendingUpload(state.promptBoxId, item.localId);
  item.resolveFn(response);
  config.onAttachmentResolved?.(resolved);
};

const onUploadError = <TResponse, TAttachment extends PendingCapableAttachment>(
  item: UploadableFileItem<TResponse, TAttachment>,
  state: FileUploadPipelineState,
  config: FileUploadPipelineConfig<TResponse, TAttachment>,
  error: unknown
): void => {
  state.markPendingAttachmentFailed(item.localId, config.describeUploadFailure?.(error));
  item.rejectFn();
  config.onUploadFailed?.(error);
};

const filterFiles = (files: File[], maxSize: number): FilteredFiles => {
  const result: FilteredFiles = {accepted: [], oversized: []};

  files.forEach((file) => {
    if (file.size > maxSize) {
      result.oversized.push(file);
      return;
    }
    result.accepted.push(file);
  });

  return result;
};

const showOversizedFileGrowl = (count: number): void => {
  const text =
    count === 1
      ? window.i18next.t('pmwjs_upload_some_media_file_exceeding_limit', {uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize()})
      : window.i18next.t('pmwjs_upload_some_media_files_exceeding_limit', {numfiles: count, uploadFileSizeLimit: getReadableMaxAllowedUploadFileSize()});

  showMessageGrowl({
    type: GROWL_TYPE.DANGER,
    text,
    showCloseIcon: true,
    interval: ERROR_GROWL_INTERVAL_MS,
  });
};

const showAttachmentLimitGrowl = (maxAttachments: number): void => {
  showMessageGrowl({
    type: GROWL_TYPE.DANGER,
    text: window.i18next.t('pmwjs_ai_chat_attachments_limit_exceeded', {max: maxAttachments}),
    showCloseIcon: true,
    interval: ERROR_GROWL_INTERVAL_MS,
  });
};
